import React from "react";
import { Badge } from "@shared/ui/badge";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { cn } from "@shared/lib/utils";
import { Consumer } from "@features/cx/consumerManagement/types";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { formatTerritoryAddress } from "@shared/utils/address";
import MaskedSSN from "@features/cx/shared/components/MaskedSSN";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import { Card, CardContent } from "@shared/ui/card";
import { DetailsSection } from "@features/cx/shared/components/DetailCard";

interface ConsumerInfoCardProps {
  consumer: Consumer;
  className?: string;
}

export const ConsumerInfoCard = ({
  consumer,
  className,
}: ConsumerInfoCardProps) => {
  const CURRENCY = useCurrency();

  console.log("consumerInfo", consumer);

  // =========================
  // Derived Data
  // =========================
  const name =
    `${consumer?.firstName ?? ""} ${consumer?.lastName ?? ""}`.trim() || "NA";
  const shortName =
    (consumer?.firstName?.[0] || "") + (consumer?.lastName?.[0] || "") || "NA";
  const outstanding = consumer?.billData?.outstandingBalance ?? "0";
  const creditAvailable = consumer?.creditAvailable;
  const statusText = consumer?.statusDisplay || "Active";

  // Configuration groups for ValueLabels
  const accountDetails = [
    { label: "Category", value: consumer?.categoryDisplay || "NA" },
    { label: "Sub Category", value: consumer?.subCategoryDisplay || "NA" },
    { label: "Plan", value: consumer?.planDetails?.planName || "NA" },
    { label: "SSN/Tax No.", value: <MaskedSSN ssn={consumer?.ssnEncrypted} /> },
    { label: "Account Type", value: consumer?.ownershipDisplay || "NA" },
  ];

  const contactDetails = [
    { label: "Email", value: consumer?.email || "NA" },
    { label: "Phone", value: consumer?.contactNumber || "NA" },
    { label: "Date of Birth", value: consumer?.dateOfBirth },
    {
      label: "Preferred Contact Method",
      value:
        consumer?.preferredContactMethod?.map((method) => method).join(", ") ||
        "NA",
    },
  ];

  const paymentDetails = [
    { label: "Invoice No", value: consumer?.billData?.invoiceNo || "NA" },
    {
      label: "Last Payment",
      value: `${CURRENCY} ${consumer?.billData?.lastPayAmount ?? "0.00"}`,
    },
    {
      label: "Last Payment On",
      value: consumer?.billData?.lastPaymentDate || "NA",
    },
    {
      label: "Payment Mode",
      value: consumer?.billData?.paymentModeDisplay || "NA",
    },
  ];

  const billData = consumer?.billData;
  const rawBillCount = billData?.billCount ?? consumer?.billCount;

  const hasBillData = Boolean(
    billData &&
      (rawBillCount === undefined || Number(rawBillCount) > 0) &&
      (
        (billData.invoiceNo && billData.invoiceNo !== "NA") ||
        (billData.lastPaymentDate && billData.lastPaymentDate !== "NA") ||
        (billData.paymentModeDisplay && billData.paymentModeDisplay !== "NA") ||
        (Array.isArray(billData.paymentData) && billData.paymentData.length > 0)
      )
  );

  return (
    <Card className={cn("bg-white shadow-sm border p-6 md:p-8", className)}>
      <CardContent className="p-0 space-y-6">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row items-start justify-between gap-6 pb-6 border-b border-gray-100">
          <div className="flex items-center gap-5">
            {/* Avatar */}
            <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xl uppercase shadow-sm">
              {shortName}
            </div>

            {/* Name + Account */}
            <div className="space-y-1">
              <h2 className="text-xl font-bold text-gray-900 leading-none">
                {name}
              </h2>
              <p className="text-sm text-gray-500 font-medium">
                Account No:{" "}
                <span className="font-bold text-gray-900">
                  {consumer?.consumerNo ?? "NA"}
                </span>
              </p>

              <div className="flex flex-wrap gap-2 mt-3">
                <Badge
                  className={cn(
                    "px-3 py-0.5 rounded text-xs border font-medium capitalize",
                    getStatusBadgeClass(statusText),
                  )}
                >
                  {statusText}
                </Badge>
                {consumer?.tags?.map((tag) => (
                  <Badge
                    key={tag}
                    variant="outline"
                    className="bg-gray-50 text-gray-600 border-gray-200 px-3 py-0.5 rounded text-xs lowercase font-normal"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {/* Credit + Outstanding */}
          <div className="flex gap-4">
            <div className="border border-gray-200 rounded-xl px-6 py-3 bg-gray-50/30 shadow-sm transition-all hover:shadow-md hover:border-blue-200 group">
              <ValueLabel
                label="Credit Available"
                className="text-center"
                value={
                  <div className="flex items-center justify-center gap-2 mt-1">
                    <span className="text-2xl font-black text-green-600">
                      {CURRENCY} {creditAvailable}
                    </span>
                  </div>
                }
              />
            </div>
            <div className="border border-gray-200 rounded-xl px-6 py-3 bg-gray-50/30 shadow-sm transition-all hover:shadow-md hover:border-red-200 group">
              <ValueLabel
                label="Total Outstandings"
                className="text-center"
                value={
                  <div className="flex items-center justify-center gap-2 mt-1">
                    <span className="text-2xl font-black text-red-600">
                      {CURRENCY} {outstanding}
                    </span>
                  </div>
                }
              />
            </div>
          </div>
        </div>

        {/* Account Details */}
        <DetailsSection
          title="Account Details"
          fields={accountDetails}
          columns={5}
        />

        {/* Contact Details */}
        <DetailsSection
          title="Contact Details"
          fields={contactDetails}
          columns={4}
        />

        {/* Payment Details */}
        <DetailsSection
          title="Bill Payment Details"
          fields={hasBillData ? paymentDetails : undefined}
          emptyMessage={!hasBillData ? "No bill generated" : undefined}
          columns={4}
        />
      </CardContent>
    </Card>
  );
};
